import type { ColumnType } from 'antd/es/table';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { userPreferencesService } from '../services/userPreferencesService';
import type {
  TableColumnSettings,
  TablePaginationSettings,
  TableSettings,
  TableSettingsState,
  TableSortRule,
  TableSortSettings,
} from '../types/table';
import { DEFAULT_PAGINATION_SETTINGS, DEFAULT_SORT_SETTINGS } from '../types/table';

/**
 * Default pagination settings (imported from types)
 */
const DEFAULT_PAGINATION: TablePaginationSettings = DEFAULT_PAGINATION_SETTINGS;

/**
 * Debounce helper for API calls
 */
const syncTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};

const debouncedSyncToAPI = (tableId: string, settings: TableSettings) => {
  // Clear existing timeout for this table
  if (syncTimeouts[tableId]) {
    clearTimeout(syncTimeouts[tableId]);
  }

  // Set new timeout to sync after 1 second of inactivity
  syncTimeouts[tableId] = setTimeout(async () => {
    try {
      await userPreferencesService.updateTableSettings(tableId, settings);
    } catch (error) {
      console.warn('Failed to sync table settings to API:', error);
      // Fail silently - localStorage will persist the changes
    }
  }, 1000);
};

/**
 * Zustand store for managing table settings with localStorage persistence
 */
export const useTableSettingsStore = create<TableSettingsState>()(
  persist(
    (set, get) => ({
      settings: {},

      /**
       * Get settings for a specific table
       */
      getTableSettings: (tableId: string) => {
        return get().settings[tableId];
      },

      /**
       * Initialize table settings if not already initialized
       */
      initializeTableSettings: (
        tableId: string,
        columns: ColumnType<any>[],
        defaultPagination?: Partial<TablePaginationSettings>,
      ) => {
        const existingSettings = get().settings[tableId];

        // If settings already exist, merge new columns
        if (existingSettings) {
          const existingColumnKeys = Object.keys(existingSettings.columns);
          const newColumns: { [key: string]: TableColumnSettings } = {};

          columns.forEach((col, index) => {
            const columnKey = (col.key || col.dataIndex) as string;
            if (!columnKey) return;

            // If column already exists, preserve its settings
            if (existingColumnKeys.includes(columnKey)) {
              newColumns[columnKey] = existingSettings.columns[columnKey];
            } else {
              // New column, add with defaults
              newColumns[columnKey] = {
                dataIndex: col.dataIndex as string,
                visible: true,
                width: col.width as number | undefined,
                order: index,
              };
            }
          });

          // Update settings with merged columns
          set((state) => ({
            settings: {
              ...state.settings,
              [tableId]: {
                ...existingSettings,
                columns: newColumns,
                lastUpdated: Date.now(),
              },
            },
          }));
          return;
        }

        // First time initialization
        const columnSettings: { [key: string]: TableColumnSettings } = {};

        columns.forEach((col, index) => {
          const columnKey = (col.key || col.dataIndex) as string;
          if (!columnKey) return;

          columnSettings[columnKey] = {
            dataIndex: col.dataIndex as string,
            visible: true,
            width: col.width as number | undefined,
            order: index,
          };
        });

        const newSettings: TableSettings = {
          columns: columnSettings,
          pagination: { ...DEFAULT_PAGINATION, ...defaultPagination },
          sort: { ...DEFAULT_SORT_SETTINGS },
          lastUpdated: Date.now(),
        };

        set((state) => ({
          settings: {
            ...state.settings,
            [tableId]: newSettings,
          },
        }));
      },

      /**
       * Update settings for a specific column
       */
      updateColumnSettings: (
        tableId: string,
        columnKey: string,
        settings: Partial<TableColumnSettings>,
      ) => {
        set((state) => {
          const tableSettings = state.settings[tableId];
          if (!tableSettings) return state;

          const updatedSettings = {
            settings: {
              ...state.settings,
              [tableId]: {
                ...tableSettings,
                columns: {
                  ...tableSettings.columns,
                  [columnKey]: {
                    ...tableSettings.columns[columnKey],
                    ...settings,
                  },
                },
                lastUpdated: Date.now(),
              },
            },
          };

          // Sync to API with debounce
          debouncedSyncToAPI(tableId, updatedSettings.settings[tableId]);

          return updatedSettings;
        });
      },

      /**
       * Update pagination settings
       */
      updatePaginationSettings: (tableId: string, pagination: Partial<TablePaginationSettings>) => {
        set((state) => {
          const tableSettings = state.settings[tableId];
          if (!tableSettings) return state;

          const updatedSettings = {
            settings: {
              ...state.settings,
              [tableId]: {
                ...tableSettings,
                pagination: {
                  ...tableSettings.pagination,
                  ...pagination,
                },
                lastUpdated: Date.now(),
              },
            },
          };

          // Sync to API with debounce
          debouncedSyncToAPI(tableId, updatedSettings.settings[tableId]);

          return updatedSettings;
        });
      },

      /**
       * Update multi-sort rules
       */
      updateMultiSort: (tableId: string, multiSort: TableSortRule[]) => {
        set((state) => {
          const tableSettings = state.settings[tableId];
          if (!tableSettings) return state;

          const updatedSettings = {
            settings: {
              ...state.settings,
              [tableId]: {
                ...tableSettings,
                sort: {
                  ...tableSettings.sort,
                  multiSort,
                },
                lastUpdated: Date.now(),
              },
            },
          };

          debouncedSyncToAPI(tableId, updatedSettings.settings[tableId]);
          return updatedSettings;
        });
      },

      /**
       * Update sort settings
       */
      updateSortSettings: (tableId: string, sort: TableSortSettings) => {
        set((state) => {
          const tableSettings = state.settings[tableId];
          if (!tableSettings) return state;

          const updatedSettings = {
            settings: {
              ...state.settings,
              [tableId]: {
                ...tableSettings,
                sort: {
                  ...tableSettings.sort,
                  ...sort,
                },
                lastUpdated: Date.now(),
              },
            },
          };

          // Sync to API with debounce
          debouncedSyncToAPI(tableId, updatedSettings.settings[tableId]);

          return updatedSettings;
        });
      },

      /**
       * Toggle column visibility
       */
      toggleColumnVisibility: (tableId: string, columnKey: string, visible: boolean) => {
        get().updateColumnSettings(tableId, columnKey, { visible });
      },

      /**
       * Update column order
       */
      updateColumnOrder: (tableId: string, columnKey: string, newOrder: number) => {
        get().updateColumnSettings(tableId, columnKey, { order: newOrder });
      },

      /**
       * Update column width
       */
      updateColumnWidth: (tableId: string, columnKey: string, width: number) => {
        get().updateColumnSettings(tableId, columnKey, { width });
      },

      /**
       * Reset settings for a specific table
       */
      resetTableSettings: (tableId: string) => {
        set((state) => {
          const newSettings = { ...state.settings };
          delete newSettings[tableId];
          return { settings: newSettings };
        });

        // Sync to API
        userPreferencesService.resetTableSettings(tableId).catch((error) => {
          console.warn('Failed to reset table settings on API:', error);
        });
      },

      /**
       * Reset all table settings
       */
      resetAllSettings: () => {
        set({ settings: {} });
      },

      /**
       * Load settings from API and merge with local storage
       */
      loadFromAPI: async () => {
        try {
          const preferences = await userPreferencesService.getUserPreferences();
          if (preferences.tableSettings) {
            set((state) => ({
              settings: {
                ...state.settings,
                ...preferences.tableSettings,
              },
            }));
          }
        } catch (error) {
          console.warn('Failed to load table settings from API:', error);
          // Keep using localStorage settings
        }
      },
    }),
    {
      name: 'table-settings-storage',
      partialize: (state) => ({
        settings: state.settings,
      }),
    },
  ),
);
